<script setup>
import { shared } from './shared.js'
</script>

<template>
    <div v-if="deviceDetail.bms" class="card" style="margin-bottom: 20px">
        <div class="card-header" style="background-color:#d1e7dd">
            <div class="form-check form-check-inline float-end" v-if="check_protocol()">
                <input class="form-check-input" type="checkbox" v-model="show_raw">
                <label class="form-label" style="margin-bottom: 0px;">Show raw</label>
            </div>
            <h5 style="margin-bottom: 0px;">BMS</h5>
        </div>
        <div class="card-body" v-show="show_raw" style="padding: 0;">
            <Vue3JsonEditor
                class="json-raw-viewer"
                v-model="deviceDetail.bms.data"
                :show-btns="false"
                :mode="'view'"
                :expandedOnStart="true"/>
        </div>
        <div v-if="check_protocol()" class="card-body">
            <span>
                <h5>Control</h5>
                <table class="table table-sm">
                    <thead>
                        <tr>
                            <th></th>
                            <th width="100px"></th>
                            <th width="150px"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Power mode</td>
                            <td class="fw-bold text-end">{{ get_data("Strat_Mode_User") }}</td>
                            <td v-if="shared.allow_control">
                                <div class="input-group input-group-sm">
                                    <select v-model="target_power_mode" class="form-select">
                                        <option value="-1">-1: AUTO</option>
                                        <option value="0">0: OFF</option>
                                        <option value="1">1: ACS</option>
                                        <option value="2">2: EXT_REF</option>
                                        <option value="3">3: IREF</option>
                                        <option value="4">4: IDLE</option>
                                        <option value="5">5: BACKUP</option>
                                    </select>
                                    <button type="button" class="btn btn-outline-danger" @click="writeParameter('Strat_Mode_User', target_power_mode)">></button>
                                </div>
                                
                            </td>
                        </tr>
                        <tr v-if="target_power_mode == 2 || get_data('Strat_PowCtrl.opMode') == 2">
                            <td>External reference</td>
                            <td class="fw-bold text-end">{{ get_data("Strat_PowCtrl.Pref") }} W</td>
                            <td v-if="shared.allow_control">
                                <input class="form-control form-control-sm" v-model="target_ext_ref"
                                    v-on:keyup.enter="writeParameter('AB_Pref_User', target_ext_ref)" />
                            </td>
                            <td v-if="shared.allow_control">
                                <button type="button" class="btn btn-danger btn-sm"
                                    @click="writeParameter('AB_Pref_User', target_ext_ref)"></button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </span>
            <div class="row">
                <div class="col-md-7">
                    <h5>System</h5>
                    <table class="table table-sm">
                        <thead>
                            <tr>
                                <th width="250px"></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>State of charge</td>
                                <td class="fw-bold">{{ get_data("BMS_Measurement.SoC", 1, 0.5) }} %</td>
                            </tr>
                            <tr>
                                <td>State of health</td>
                                <td>{{ get_data("BMS_Measurement.SoH", 1, 0.5) }} %</td>
                            </tr>
                            <tr class="table-group-divider">
                                <td>SysStat</td>
                                <td>{{ get_data("SysInfo.SysStat") }}</td>
                            </tr>
                            <tr>
                                <td>SysErr</td>
                                <td>{{ get_data("SysInfo.SysErr.all") }}</td>
                            </tr>
                            <tr class="table-group-divider">
                                <td class="text-start">Battery charge limit</td>
                                <td>{{ get_data("Strat_PowCtrl.PmaxCharge", 0) }} W</td>
                            </tr>
                            <tr>
                                <td class="text-start">Battery discharge limit</td>
                                <td>{{ get_data("Strat_PowCtrl.PmaxDischarge", 0) }} W</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="col-md-5">
                    <h5>DCDC</h5>
                    <table class="table table-sm text-end">
                        <thead>
                            <tr>
                                <th width="200px"></th>
                                <th width="100px"></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td class="text-start">DC link voltage</td>
                                <td>{{ get_data("PB_DCInfo.VDC", 1, 0.1) }} V</td>
                            </tr>
                            <tr class="table-group-divider">
                                <td class="text-start">Battery voltage</td>
                                <td>{{ get_data("PB_BatInfo.VBat", 1, 0.1) }} V</td>
                            </tr>
                            <tr>
                                <td class="text-start">Battery current</td>
                                <td>{{ get_data("PB_BatInfo.IBat", 2, 0.01) }} A</td>
                            </tr>
                            <tr>
                                <td class="text-start">Battery power</td>
                                <td>{{ get_data("PB_BatInfo.PBat", 0) }} W</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            
            
            <h5>Battery cells</h5>
            <table class="table table-sm text-end">
                <thead>
                    <tr>
                        <th width="200px"></th>
                        <th width="100px">Min</th>
                        <th width="100px">Mean</th>
                        <th width="100px">Max</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td class="text-start">Cell voltage</td>
                        <td>{{ get_data("BMS_Measurement.VCellMin", 0) }} mV</td>
                        <td>{{ get_data("BMS_Measurement.VCellMean", 0) }} mV</td>
                        <td>{{ get_data("BMS_Measurement.VCellMax", 0) }} mV</td>
                    </tr>
                    <tr>
                        <td class="text-start">Cell temperature</td>
                        <td>{{ get_data("BMS_Measurement.TCellMin", 1, 0.1) }} °C</td>
                        <td>{{ get_data("BMS_Measurement.TCellMean", 1, 0.1) }} °C</td>
                        <td>{{ get_data("BMS_Measurement.TCellMax", 1, 0.1) }} °C</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
export default {
    props: ['deviceDetail', 'client'],
    setup(props) { },
    data() {
        return {
            show_raw: 0,

            target_power_mode: 1,
            target_ext_ref: 0,
        };
    },
    methods: {
        check_protocol() {
            return this.deviceDetail.info && this.deviceDetail.info.protocol_version != null
        },
        get_data(parameter, round = null, multiplier = null) {
            return shared.get_nested_value_safely(this.deviceDetail.bms.data, parameter, round, multiplier)
        },
        writeParameter(parameter, value) {
            var topic = this.deviceDetail.topic_base + "/" + this.deviceDetail.serial + "/request"
            var payload = {
                function: "write_parameter",
                target: "bms",
                parameter: parameter,
                value: value,
            }
            console.log({ topic: topic, payload: payload })
            this.client.publish(topic, JSON.stringify(payload), error => {
                if (error) {
                    console.log('Publish error', error)
                }
            })
        }
    },
    created() {
        if (this.deviceDetail.info.protocol_version == null) {
            this.show_raw = 1;
        }
    },
};
</script>